import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
    BriefcaseBusiness,
    Building2,
    CalendarDays,
    Download,
    FileSpreadsheet,
    IndianRupee,
    Printer,
    RefreshCcw,
    Search,
    ShieldCheck,
    Sparkles,
    Users
} from 'lucide-react';
import api from '../../utils/api';
import { API_BASE_URL } from '../../utils/apiBase';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar/sidebar';
import Pagination from '../../components/Pagination/Pagination';
import { toast } from '../../components/notifications/notificationService';
import './premiumRegister.css';

const MONEY_COLUMNS = new Set([
    'Amount Received',
    'Gross Premium',
    'GST Amount',
    'Net Premium',
    'Total Brokerage'
]);

const TABLE_COLUMNS = [
    'Policy Number',
    'Record Type',
    'Endorsement Number',
    'Endorsement Type',
    'Client Name',
    'Client Type',
    'Transaction Date',
    'Policy Type',
    'Class of Policy',
    'Insurance Company',
    'Insurance Company Branch',
    'Business Type',
    'Coverage Type',
    'Policy Start Date',
    'Policy End Date',
    'Gross Premium',
    'Net Premium',
    'Total Brokerage',
    'Record Status',
    'Created By'
];

const parseNumber = (value) => {
    const number = Number(String(value ?? '').replace(/,/g, ''));
    return Number.isFinite(number) ? number : 0;
};

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
}).format(value || 0);

const uniqueValues = (rows, key) => [...new Set(rows.map((row) => row[key]).filter(Boolean))]
    .sort((a, b) => String(a).localeCompare(String(b)));

const BusinessStatements = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [recordType, setRecordType] = useState('');
    const [clientType, setClientType] = useState('');
    const [insurer, setInsurer] = useState('');
    const [policyType, setPolicyType] = useState('');
    const [client, setClient] = useState('');
    const [status, setStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    const fetchData = useCallback(async (params = {}) => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get(`${API_BASE_URL}/api/business-statements`, { params });
            setReportData(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Failed to load Business Statements data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const insurerOptions = useMemo(() => uniqueValues(reportData, 'Insurance Company'), [reportData]);
    const policyOptions = useMemo(() => uniqueValues(reportData, 'Policy Type'), [reportData]);
    const clientOptions = useMemo(() => uniqueValues(reportData, 'Client Name'), [reportData]);
    const statusOptions = useMemo(() => uniqueValues(reportData, 'Record Status'), [reportData]);
    const clientTypeOptions = useMemo(() => uniqueValues(reportData, 'Client Type'), [reportData]);

    const filteredData = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        return reportData.filter((row) => {
            const searchable = Object.values(row).join(' ').toLowerCase();
            return (!query || searchable.includes(query))
                && (!recordType || row['Record Type'] === recordType)
                && (!clientType || row['Client Type'] === clientType)
                && (!insurer || row['Insurance Company'] === insurer)
                && (!policyType || row['Policy Type'] === policyType)
                && (!client || row['Client Name'] === client)
                && (!status || row['Record Status'] === status);
        });
    }, [reportData, searchTerm, recordType, clientType, insurer, policyType, client, status]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, recordType, clientType, insurer, policyType, client, status, itemsPerPage]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
        setCurrentPage((page) => Math.min(page, totalPages));
    }, [filteredData.length, itemsPerPage]);

    const pageLast = currentPage * itemsPerPage;
    const pageFirst = pageLast - itemsPerPage;
    const currentData = filteredData.slice(pageFirst, pageLast);

    const summary = useMemo(() => ({
        policies: filteredData.filter((row) => row['Record Type'] === 'Policy').length,
        endorsements: filteredData.filter((row) => row['Record Type'] === 'Endorsement').length,
        clients: new Set(filteredData.map((row) => row['Client Name']).filter(Boolean)).size,
        insurers: new Set(filteredData.map((row) => row['Insurance Company']).filter(Boolean)).size,
        grossPremium: filteredData.reduce((sum, row) => sum + parseNumber(row['Gross Premium']), 0),
        netPremium: filteredData.reduce((sum, row) => sum + parseNumber(row['Net Premium']), 0)
    }), [filteredData]);

    const applyDateRange = () => {
        if ((startDate && !endDate) || (!startDate && endDate)) {
            setError('Please select both start date and end date.');
            return;
        }
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            setError('Start date cannot be after end date.');
            return;
        }

        fetchData(startDate && endDate ? { start: startDate, end: endDate } : {});
    };

    const resetFilters = () => {
        setStartDate('');
        setEndDate('');
        setRecordType('');
        setClientType('');
        setInsurer('');
        setPolicyType('');
        setClient('');
        setStatus('');
        setSearchTerm('');
        fetchData();
    };

    const exportToExcel = () => {
        if (!filteredData.length) {
            setError('No policy or endorsement rows are available to export.');
            return;
        }
        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Business Statements');
        XLSX.writeFile(workbook, `Business_Statements_${startDate || 'ALL'}_to_${endDate || 'ALL'}.xlsx`);
        toast.success('Business Statements Excel downloaded');
    };

    const summaryCards = [
        { label: 'Total Policies', value: summary.policies, note: 'Submitted policies', icon: BriefcaseBusiness, tone: 'is-blue' },
        { label: 'Endorsements', value: summary.endorsements, note: 'All endorsement statuses', icon: ShieldCheck, tone: 'is-green' },
        { label: 'Clients', value: summary.clients, note: 'Unique clients', icon: Users, tone: 'is-amber' },
        { label: 'Insurers', value: summary.insurers, note: 'Unique insurers', icon: Building2, tone: 'is-teal' },
        { label: 'Gross Premium', value: formatCurrency(summary.grossPremium), note: 'Policies plus endorsements', icon: IndianRupee, tone: 'is-indigo' },
        { label: 'Net Premium', value: formatCurrency(summary.netPremium), note: 'Policies plus endorsements', icon: IndianRupee, tone: 'is-rose' }
    ];

    return (
        <div className="premium-register-container premium-register-shell">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`premium-register-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="premium-register-sticky-header"><Header /></div>

                <main className="premium-register-scroll-area pr-page">
                    <section className="pr-hero">
                        <div>
                            <div className="pr-eyebrow"><Sparkles size={15} /> Reports Dashboard</div>
                            <h1>Business Statements</h1>
                            <p>Complete submitted-policy and endorsement data across all dates, clients, policy types, and insurers.</p>
                        </div>
                        <div className="pr-hero-stat">
                            <span>Matched Records</span>
                            <strong>{filteredData.length}</strong>
                            <small>{reportData.length} loaded</small>
                        </div>
                    </section>

                    <section className="pr-summary-grid">
                        {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
                            <div className={`pr-summary-card ${tone}`} key={label}>
                                <Icon size={22} />
                                <span>{label}</span>
                                <strong>{value}</strong>
                                <small>{note}</small>
                            </div>
                        ))}
                    </section>

                    <section className="pr-filter-card">
                        <div className="pr-filter-header">
                            <div>
                                <h2>Statement Filters</h2>
                                <p>All filters default to every submitted policy and every endorsement, regardless of endorsement status.</p>
                            </div>
                            <div className="pr-search-wrap">
                                <Search size={18} />
                                <input
                                    type="search"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder="Search any policy or endorsement field"
                                />
                            </div>
                        </div>

                        {error && <div className="pr-alert">{error}</div>}

                        <div className="pr-filter-grid">
                            <label>
                                <span>Transaction Date Range</span>
                                <div className="pr-date-range">
                                    <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                                    <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                                </div>
                            </label>
                            <label>
                                <span>Record Type</span>
                                <select value={recordType} onChange={(event) => setRecordType(event.target.value)}>
                                    <option value="">Policies & Endorsements</option>
                                    <option value="Policy">Policies Only</option>
                                    <option value="Endorsement">Endorsements Only</option>
                                </select>
                            </label>
                            <label>
                                <span>Client Type</span>
                                <select value={clientType} onChange={(event) => setClientType(event.target.value)}>
                                    <option value="">All Client Types</option>
                                    {clientTypeOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>Client</span>
                                <select value={client} onChange={(event) => setClient(event.target.value)}>
                                    <option value="">All Clients</option>
                                    {clientOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>Insurer</span>
                                <select value={insurer} onChange={(event) => setInsurer(event.target.value)}>
                                    <option value="">All Insurers</option>
                                    {insurerOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>Policy Type</span>
                                <select value={policyType} onChange={(event) => setPolicyType(event.target.value)}>
                                    <option value="">All Policies</option>
                                    {policyOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>Record Status</span>
                                <select value={status} onChange={(event) => setStatus(event.target.value)}>
                                    <option value="">All Statuses</option>
                                    {statusOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                                </select>
                            </label>
                        </div>

                        <div className="pr-action-row">
                            <button className="pr-btn pr-btn-primary" onClick={applyDateRange} disabled={loading}>
                                {loading ? <RefreshCcw className="pr-spin" size={17} /> : <CalendarDays size={17} />}
                                {loading ? 'Loading...' : 'Apply Date / All Dates'}
                            </button>
                            <button className="pr-btn pr-btn-success" onClick={exportToExcel} disabled={!filteredData.length}>
                                <FileSpreadsheet size={17} /> Export Complete Excel
                            </button>
                            <button className="pr-btn pr-btn-ghost" onClick={() => window.print()} disabled={!filteredData.length}>
                                <Printer size={17} /> Print / PDF
                            </button>
                            <button className="pr-btn pr-btn-light" onClick={resetFilters} disabled={loading}>
                                <RefreshCcw size={17} /> Reset to All Data
                            </button>
                        </div>
                    </section>

                    <section className="pr-table-card">
                        <div className="pr-table-toolbar">
                            <div>
                                <h2>Complete Policy and Endorsement Business</h2>
                                <p>{filteredData.length} records matched from {reportData.length} loaded records</p>
                            </div>
                            <span><Download size={15} /> Full details included in Excel</span>
                        </div>

                        <div className="pr-table-scroll">
                            <table className="pr-report-table">
                                <thead>
                                    <tr>
                                        {TABLE_COLUMNS.map((column) => (
                                            <th key={column} className={column === 'Policy Number' ? 'pr-sticky-col pr-sticky-policy' : ''}>
                                                {column}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? Array.from({ length: 8 }).map((_, rowIndex) => (
                                        <tr key={`skeleton-${rowIndex}`} className="pr-skeleton-row">
                                            {TABLE_COLUMNS.map((column) => <td key={column}><span /></td>)}
                                        </tr>
                                    )) : currentData.length === 0 ? (
                                        <tr>
                                            <td className="pr-empty-state" colSpan={TABLE_COLUMNS.length}>
                                                <BriefcaseBusiness size={34} />
                                                <strong>No business records found</strong>
                                                <span>Reset the filters to return to all submitted policies and endorsements.</span>
                                            </td>
                                        </tr>
                                    ) : currentData.map((row, index) => (
                                        <tr key={`${row['Policy Number'] || 'policy'}-${pageFirst + index}`}>
                                            {TABLE_COLUMNS.map((column) => (
                                                <td
                                                    key={column}
                                                    data-label={column}
                                                    className={`${column === 'Policy Number' ? 'pr-sticky-col pr-sticky-policy' : ''} ${column === 'Client Name' ? 'pr-client-cell' : ''} ${MONEY_COLUMNS.has(column) ? 'pr-amount-cell' : ''}`}
                                                >
                                                    {MONEY_COLUMNS.has(column) ? formatCurrency(parseNumber(row[column])) : (row[column] || '-')}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {filteredData.length > 0 && (
                            <div className="pr-pagination-row">
                                <div className="pr-pagination-meta">
                                    <span>Showing {pageFirst + 1}-{Math.min(pageLast, filteredData.length)} of {filteredData.length}</span>
                                    <small>Filtered policies and endorsements</small>
                                </div>
                                <div className="pr-pagination-controls">
                                    <Pagination
                                        totalItems={filteredData.length}
                                        itemsPerPage={itemsPerPage}
                                        currentPage={currentPage}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>
                                <div className="pr-items-per-page">
                                    <label>Items per page:</label>
                                    <select value={itemsPerPage} onChange={(event) => setItemsPerPage(Number(event.target.value))}>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

export default BusinessStatements;
